import type { Cell, CombatantId, ForcedMoveCollision } from "@lancer-vtt/domain";
import type { AttackRollFact, GameEvent, LedgerSettlement } from "../events/gameEvents";
import type { EventLoop } from "../events/loop";

/**
 * The **resolution-scoped fact ledger**: a single **mutable** object created once per
 * {@link import("../resolve").resolve} call and threaded **by reference** through every
 * {@link import("./triggerBus").FireContext} / {@link import("./interpret").EffectContext}
 * the resolution constructs. Every OTHER field on those two types is a frozen fact,
 * independently recomputed identically at each fire site (`weaponType`/`mount`/`natural20`/…) —
 * the ledger is the ONE place a fired atom can leave a fact for a LATER fire point in the SAME
 * `resolve()` call to read back.
 *
 * **What it holds that state can't.** The trigger bus's listeners read the event loop's folded
 * scratch (`events/loop.ts`, `decisions/0212` item 4), so a status or resource a declared-phase
 * clause writes is visible to the on-hit clauses after it. The ledger keeps the facts that are not
 * state at all: which option a `choose` took, which direction prompt was answered, which pushes
 * collided, the raised costs, the to-hit rolls. It is a plain object shared by reference across
 * every fire site this resolution constructs, mutated in place as the resolution proceeds.
 *
 * **Scope: the declared `choose` result.** {@link import("./interpret").interpretChoose}
 * records which option id a `choose` atom ACTUALLY resolved to (the recorded answer, or the
 * silent first-option default — whichever the atom really took), keyed by the atom's own
 * `prompt`. A LATER same-resolution `modifier`/`triggered` clause's `condition` reads it back
 * via the `chose` {@link import("./predicate").Predicate} op (`key` = the `choose` atom's
 * `prompt`, `value` = the expected option id) — so `t_walking_armory:1`'s ammo choice (an
 * `on_attack_declared` `choose`) can gate the SAME attack's `override_attack_damage_type`
 * modifier AND its on-hit Knockback/Arcing riders. Recording the atom's ACTUAL decision (not
 * the raw, possibly-absent `intent.choices` map) matters: a `choose` with no recorded answer
 * silently takes its first option — a predicate reading `intent.choices` directly would misread
 * that case as "nothing chosen," while the ledger records exactly what happened.
 *
 * **Designed to grow:** the ledger is the substrate future cross-firing content rides — a
 * follow-up primitive that needs to know "which weapon fired" / "which target was hit" by the
 * triggering attack adds a new optional field here (never repurpose `choices`), writes it at
 * the fire site that knows the fact, and reads it through a new predicate op mirroring `chose`'s
 * shape.
 */
/** One push that stopped short this resolution: the pushed `mover` and its {@link ForcedMoveCollision}. */
export type LedgerCollision = ForcedMoveCollision & { mover: CombatantId };

export type ResolutionLedger = {
  /**
   * The option id a `choose` atom's `prompt` actually resolved to, written by
   * `interpretChoose` the instant it decides (BEFORE running the option's own effect atoms) —
   * so a `choose` nested inside another `choose`'s option still records outer-to-inner in call
   * order, and a later re-entrant `choose` under the SAME prompt id simply overwrites (no
   * shipped content re-prompts the same id twice in one resolution). Empty until the first
   * `choose` fires in this resolution.
   */
  choices: Record<string, string>;
  /**
   * The promptId of the direction (`"chosen"`-mode) halt this resolution's mover/chooser atom
   * actually consumed, when one did. Written by
   * `interpretMoveSelfTowardTarget`/`interpretForcedMove` (`interpret.ts`) the instant either
   * reads an already-recorded `ctx.toward` for a `"chosen"` mode WITHOUT halting — the SAME
   * moment a `move_toward:<id>`/`forced_move_toward:<id>` decision (if one was raised on an
   * earlier round-trip) gets answered. `resolve()` reads this back off the SAME ledger object
   * after the whole resolution completes and surfaces it on {@link
   * import("../resolve").Resolved.answeredDirectionPromptId} — the single place a caller (e.g.
   * `refereeIntent.ts`'s auto-dismiss stamp) learns "which decision did this pass just answer,"
   * rather than hand-reconstructing the promptId from `intent.target.id`, a guess that is only
   * right when the mover happens to be the target and breaks on a declared-phase self-lunge
   * (where the mover is the ATTACKER). `undefined` until (if ever) one such atom fires in this
   * resolution; a resolution with more than one such atom keeps only the LAST write — the
   * two-halts-one-slot collision is a separate, not-yet-guarded hazard, so this field inherits
   * the same single-slot limitation `ctx.toward` already has, not a new one.
   */
  answeredDirectionPromptId?: string;
  /**
   * Every `away_from` push, and every pull toward a combatant, this resolution that stopped short,
   * in emission order — written by `interpretForcedMove`, read by the `collided` predicate and an
   * area selector's `collided` flag (Juggernaut's Kinetic Mass Transfer reacts to the Ram push the
   * seed Ram entry makes; the Gravity Rifle's Prone and the Boarding Leash's grapple read a pull).
   * A pull that reaches the combatant it was pulled toward is not a collision.
   */
  collisions?: LedgerCollision[];
  /**
   * Where this resolution's `forced_move`s have left each combatant they moved, so the next push
   * on the same combatant starts there rather than from the pre-resolution snapshot (a Ram's own
   * Knockback 1, then Juggernaut rank 1's extra 2: 3 spaces in all). Written and read by
   * `interpretForcedMove`.
   */
  displaced?: Record<CombatantId, Cell>;
  /** What the ledger's owner does with the events raised into it (`events/gameEvents.ts`). */
  settlement: LedgerSettlement;
  /** The costs pending this resolution, settled once at the owner's end (`decisions/0211`). Each was raised through {@link loop} (`emitEvent`). */
  events?: GameEvent[];
  /**
   * Every to-hit roll this resolution made, in roll order: `resolveSingleTarget` records one per
   * roll, so an area fan-out records one per caught target and `attackRolls[0]` is RAW's "the first
   * attack roll you make". A replacement's `firstAttackRoll` match reads it at settlement.
   */
  attackRolls?: AttackRollFact[];
  /** Set by `settleEvents`; raising an event after it throws. */
  settled?: true;
  /** The intent's event loop, when the resolution's caller holds one: every fire this resolution makes raises into it. */
  loop?: EventLoop;
  /**
   * The loop log's length when this resolution began. A fact the resolution reads off the loop (a
   * declared reaction's `attack_forced_miss`) is one raised after it, so a Barrage's second shot or a
   * later forced attack never reads the first's.
   */
  opened?: number;
};

/**
 * A fresh, empty ledger — call once per `resolve()` (or a future `resolveActivate`) invocation.
 * The caller declares what happens to events raised into it, so none can be silently lost.
 */
export function createResolutionLedger(settlement: LedgerSettlement): ResolutionLedger {
  return { choices: {}, settlement };
}
